/**
 * MagTek (Magensa UniGate) credentials in Super Admin — pure rules.
 * Values are WRITE-ONLY: the API returns status ({set, environment, baseUrlHost…}
 * or {source, set}), never a credential, and the forms start empty every time.
 *
 * Processor names are fine here (Super Admin only — owner rule 4).
 */
/** The base URL the lane was built and Pilot-tested against (pos-magtek runbook: pilot-smoke default). */
export const PILOT_UNIGATE_URL = 'https://svc1.magensa.net/Unigate';
/** Route of the platform settings screen (a child of /pos-config, so its nav permission covers it). */
export const PAYMENT_PROCESSORS_PATH = '/pos-config/payment-processors';

export type UnigateEnv = 'pilot' | 'live';
export interface CredForm { customerCode: string; username: string; password: string; environment: UnigateEnv; baseUrl: string }

export const emptyCredForm = (): CredForm => ({ customerCode: '', username: '', password: '', environment: 'pilot', baseUrl: PILOT_UNIGATE_URL });

/** Switching environment prefills the documented URL (Pilot) or clears it (Live is not documented — required). */
export function withEnvironment(f: CredForm, env: UnigateEnv): CredForm {
  const wasDefault = !f.baseUrl.trim() || f.baseUrl.trim() === PILOT_UNIGATE_URL;
  return { ...f, environment: env, baseUrl: wasDefault ? (env === 'pilot' ? PILOT_UNIGATE_URL : '') : f.baseUrl };
}

export function credProblems(f: CredForm): Partial<Record<keyof CredForm, string>> {
  const out: Partial<Record<keyof CredForm, string>> = {};
  if (!f.customerCode.trim()) out.customerCode = 'Required';
  if (!f.username.trim()) out.username = 'Required';
  if (!f.password) out.password = 'Required';
  const u = f.baseUrl.trim();
  if (!u) out.baseUrl = f.environment === 'live' ? 'Enter the Live UniGate URL' : 'Required';
  else if (!/^https:\/\/[^\s/]+/i.test(u)) out.baseUrl = 'Must start with https://';
  return out;
}

export function credBody(f: CredForm, confirmHost = false) {
  return {
    customerCode: f.customerCode.trim(), username: f.username.trim(), password: f.password,
    environment: f.environment, baseUrl: f.baseUrl.trim(), ...(confirmHost ? { confirmHost: true } : {}),
  };
}

/** Platform status chip: "Set · Pilot" / "Set · Live" / "Missing". */
export function platformChip(st: any): { tone: 'ok' | 'off'; text: string } {
  if (!st?.set) return { tone: 'off', text: 'Missing' };
  return { tone: 'ok', text: `Set · ${st.environment === 'live' ? 'Live' : st.environment === 'pilot' ? 'Pilot' : '—'}` };
}

/**
 * THE ONE LINE on the SUNMI MagTek form and the location card.
 * `loc` = GET …/payment-gateway/magtek ({source, set}); undefined = loading, null = failed.
 */
export function unigateLine(loc: any): { tone: 'ok' | 'warn' | 'muted'; text: string; linkToSettings: boolean } {
  if (loc === undefined) return { tone: 'muted', text: 'UniGate: checking…', linkToSettings: false };
  if (loc === null) return { tone: 'muted', text: 'UniGate: could not check', linkToSettings: false };
  if (loc.source === 'location') return loc.set
    ? { tone: 'ok', text: 'UniGate: Own credentials · Set', linkToSettings: false }
    : { tone: 'warn', text: 'UniGate: Own credentials · Missing', linkToSettings: false };
  return loc.set
    ? { tone: 'ok', text: 'UniGate: Using platform credentials', linkToSettings: false }
    : { tone: 'warn', text: 'UniGate: Missing — set platform credentials', linkToSettings: true };
}

// ── Website (Magensa TEConnect) — PUBLIC config, shown and prefilled (unlike UniGate) ──
export type TeconnectEnv = 'sandbox' | 'production';
export interface TeconnectData { publicKey: string; googleMerchantId?: string; googleGatewayId?: string; appleMerchantId?: string; environment: TeconnectEnv }
export interface TeconnectForm { publicKey: string; googleMerchantId: string; googleGatewayId: string; environment: TeconnectEnv }

export const emptyTeconnectForm = (t?: Partial<TeconnectData> | null): TeconnectForm => ({
  publicKey: t?.publicKey || '', googleMerchantId: t?.googleMerchantId || '', googleGatewayId: t?.googleGatewayId || '',
  environment: t?.environment === 'production' ? 'production' : 'sandbox',
});

/** True when the operator typed anything beyond the default. */
export const teconnectFilled = (f: TeconnectForm) => !!(f.publicKey.trim() || f.googleMerchantId.trim() || f.googleGatewayId.trim());
export const teconnectProblem = (f: TeconnectForm): string | null => (teconnectFilled(f) && !f.publicKey.trim() ? 'Enter the public key' : null);

/** `appleMerchantId` is carried through untouched so a save here never wipes it. */
export function teconnectBody(f: TeconnectForm, keep?: Partial<TeconnectData> | null): TeconnectData {
  const out: TeconnectData = { publicKey: f.publicKey.trim(), environment: f.environment };
  if (f.googleMerchantId.trim()) out.googleMerchantId = f.googleMerchantId.trim();
  if (f.googleGatewayId.trim()) out.googleGatewayId = f.googleGatewayId.trim();
  if (keep?.appleMerchantId) out.appleMerchantId = keep.appleMerchantId;
  return out;
}

export interface MagtekReadiness { unigate: 'location' | 'platform' | null; teconnect: 'location' | 'platform' | null }

/**
 * Mirrors the backend rule (super-admin-ext3 lib/magtek-readiness): a location on its OWN
 * credentials fails closed (no platform fallback); otherwise the platform default.
 * `loc` = GET …/payment-gateway/magtek ({source, set, teconnect}); `platform` = GET platform ({set, teconnect}).
 */
export function resolveMagtekReadiness(loc: any, platform: any): MagtekReadiness {
  if (loc?.source === 'location') return { unigate: loc.set ? 'location' : null, teconnect: loc.teconnect?.publicKey ? 'location' : null };
  return { unigate: platform?.set ? 'platform' : null, teconnect: platform?.teconnect?.publicKey ? 'platform' : null };
}

export const magtekReady = (r: MagtekReadiness | null | undefined) => !!(r && r.unigate && r.teconnect);

export const readinessText = (v: 'location' | 'platform' | null) => (v === 'location' ? 'location' : v === 'platform' ? 'platform' : 'missing');
